/**
 * Create or edit a service plan.
 *
 * Editing is only offered while no engagement is on the plan. Once a client has
 * been sold it, the server refuses the edit (409 PLAN_IN_USE) and the answer is
 * to supersede — which creates v+1 and leaves existing clients on the version
 * they bought. That is what plan versioning is for.
 */

import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type ServiceLine, type ServicePlan } from '@/lib/api';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';

export function PlanFormDialog({ open, onOpenChange, plan }: {
  open: boolean; onOpenChange: (v: boolean) => void; plan?: ServicePlan | null;
}) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const editing = !!plan;
  const [serviceLineId, setServiceLineId] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [listPrice, setListPrice] = useState('');
  const [setupFee, setSetupFee] = useState('');
  const [billingFrequency, setBillingFrequency] = useState('monthly');

  useEffect(() => {
    if (!open) return;
    setServiceLineId(plan?.service_line_id ?? '');
    setName(plan?.name ?? '');
    setDescription(plan?.description ?? '');
    setListPrice(plan?.list_price != null ? String(plan.list_price) : '');
    setSetupFee(plan?.setup_fee != null ? String(plan.setup_fee) : '');
    setBillingFrequency(plan?.billing_frequency ?? 'monthly');
  }, [open, plan]);

  const { data: linesData } = useQuery({
    queryKey: ['svc', 'service-lines'],
    queryFn: () => api.getServiceLines(),
    enabled: open,
  });
  const lines = (linesData?.data ?? []) as ServiceLine[];

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        name,
        description: description || null,
        listPrice: listPrice === '' ? null : Number(listPrice),
        setupFee: setupFee === '' ? null : Number(setupFee),
        billingFrequency,
      };
      // The two calls return different payloads; only success and error matter
      // here, so they are narrowed to that.
      const r = editing
        ? await api.updateServicePlan(plan!.id, body)
        : await api.createServicePlan({ ...body, serviceLineId });
      return { success: r.success, error: r.error };
    },
    onSuccess: (r) => {
      if (!r.success) {
        toast({
          title: r.error?.code === 'PLAN_IN_USE' ? 'This plan is in use' : 'Could not save',
          description: r.error?.message,
          variant: 'destructive',
        });
        return;
      }
      qc.invalidateQueries({ queryKey: ['svc'] });
      toast({
        title: editing ? 'Plan updated' : 'Plan created',
        description: editing ? undefined : 'Add its deliverables next.',
      });
      onOpenChange(false);
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{editing ? 'Edit plan' : 'New service plan'}</DialogTitle>
          <DialogDescription>
            A priced package. Its deliverables are what each period's work is generated from.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="text-xs">Service line</Label>
            <Select value={serviceLineId} onValueChange={setServiceLineId} disabled={editing}>
              <SelectTrigger className="mt-1"><SelectValue placeholder="Select…" /></SelectTrigger>
              <SelectContent>
                {lines.map((l) => (
                  <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Name</Label>
            <Input
              className="mt-1" value={name} onChange={(e) => setName(e.target.value)}
              placeholder="Local SEO Growth"
            />
          </div>
          <div>
            <Label className="text-xs">Description</Label>
            <Textarea
              className="mt-1" rows={2} value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">
                {billingFrequency === 'one_time' ? 'Project fee' : 'List price'}
              </Label>
              <Input
                className="mt-1" type="number" min={0} step="0.01"
                value={listPrice} onChange={(e) => setListPrice(e.target.value)}
                placeholder={billingFrequency === 'one_time' ? '2500.00' : '299.00'}
              />
            </div>
            <div>
              <Label className="text-xs">Setup fee</Label>
              <Input
                className="mt-1" type="number" min={0} step="0.01"
                value={setupFee} onChange={(e) => setSetupFee(e.target.value)}
                placeholder="299.00"
              />
              <p className="text-[11px] text-muted-foreground mt-1">
                One-time, charged alongside the first period.
              </p>
            </div>
            <div>
              <Label className="text-xs">Billing frequency</Label>
              <Select value={billingFrequency} onValueChange={setBillingFrequency}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="one_time">One-time</SelectItem>
                  {['monthly', 'quarterly', 'semi_annual', 'annual'].map((f) => (
                    <SelectItem key={f} value={f} className="capitalize">
                      {f.replace('_', ' ')}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {billingFrequency === 'one_time' && (
            <p className="text-xs text-muted-foreground border-t pt-3">
              A one-time plan produces a single set of work when an engagement is
              created from it, then stops — it never regenerates. Each deliverable is
              produced once, scheduled by its "due day of cycle" offset from the start
              date, which is how a build is sequenced.
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={() => save.mutate()}
            disabled={!serviceLineId || !name || save.isPending}
          >
            {editing ? 'Save changes' : 'Create plan'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
